import React from 'react';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { NotFound } from '@/components/NotFound';
import { GlobalOverlayLoader } from '@/components/common/GlobalOverlayLoader.tsx';
import { useAuth } from '@/context/AuthContext.tsx';
import { ErrorPage } from '@/components/errors/ErrorPage';
import * as Sentry from '@sentry/react';
import { useMobileKeyboardFocusScroll } from '@/hooks/useMobileKeyboardFocusScroll';
import { queryClient } from '@/lib/queryClient';
import { makeLogger } from '@/utils/logger/logger.ts';

const logger = makeLogger('App');

// Create router
const router = createRouter({
	routeTree,
	context: { queryClient },
	defaultPreload: 'intent',
	defaultPreloadStaleTime: 0,
	defaultPendingMs: 300,
	defaultPendingMinMs: 500,
	defaultNotFoundComponent: () => {
		return <NotFound />;
	},
	defaultErrorComponent: ({ error, reset }) => {
		// Report router errors to Sentry and capture the event ID
		const eventId = Sentry.captureException(error, {
			tags: {
				error_boundary: 'router',
			},
		});

		return <ErrorPage error={error} resetErrorBoundary={reset} eventId={eventId} />;
	},
});

router.subscribe('onBeforeLoad', (route) => {
	logger.debug(`Route about to load:  ${route.toLocation.pathname}`);
});

router.subscribe('onLoad', (route) => {
	logger.debug(`Route loaded: ${route.toLocation.pathname}`);
});

router.subscribe('onResolved', (route) => {
	logger.debug(`Route resolved: ${route.toLocation.pathname}`);
});

// Register router type
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router;
	}
}

const App: React.FC = () => {
	const { isLoggingOut } = useAuth();

	// Enable mobile keyboard focus scrolling for older devices
	useMobileKeyboardFocusScroll();

	if (isLoggingOut) {
		return <GlobalOverlayLoader text="Logging you out... come back soon!" />;
	}

	return <RouterProvider router={router} />;
};

export default App;
